/**
 * The event side of the worklist + events page (GDN-2774): one encounter's
 * event stream, and the badge that summarises it in the worklist row.
 *
 * Both read the same cached probe (see `useEncounterEvents`), so the badge and
 * the expanded stream can never disagree — the badge is the stream's headline.
 *
 * The load-bearing state here is the EMPTY one. A worklist row asserts an
 * encounter is in some state; if nothing was ever recorded about it, that
 * state came from somewhere other than the event stream, and this page exists
 * to make that obvious. So "no events" is not a muted em-dash — it's the
 * loudest thing on the row.
 */
import { useEffect } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';

import { EVENT_PROBE_LIMIT, useEncounterEvents, type EventLens } from '../hooks/useEncounterEvents';
import { EventActor } from './event-actor';

const LENSES: EventLens[] = ['all', 'domain', 'audit', 'usage'];

const LENS_LABELS: Record<EventLens, string> = {
  all: 'All',
  domain: 'Domain',
  audit: 'Audit',
  usage: 'Usage',
};

const formatDateTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const shortId = (id: string): string => (id.length > 12 ? `${id.slice(0, 12)}…` : id);

/** The lens toggle, hoisted so the page can drive every row from one control. */
export const EventLensToggle = ({
  lens,
  onChange,
}: {
  lens: EventLens;
  onChange: (next: EventLens) => void;
}) => (
  <ToggleButtonGroup
    size="small"
    exclusive
    value={lens}
    onChange={(_, next: EventLens | null) => {
      if (next) onChange(next);
    }}
    aria-label="Event lens"
  >
    {LENSES.map(value => (
      <ToggleButton key={value} value={value}>
        {LENS_LABELS[value]}
      </ToggleButton>
    ))}
  </ToggleButtonGroup>
);

/** What a row's coverage probe resolved to. */
export type CoverageState = 'loading' | 'empty' | 'present' | 'unknown';

/**
 * The worklist row's coverage badge. Red and explicit when the encounter has
 * no events under the current lens; quiet otherwise.
 *
 * Reports its verdict up through `onResolve` so the page can count gaps across
 * the visible rows without duplicating the probe.
 */
export const EncounterEventCoverageChip = ({
  organizationId,
  encounterId,
  lens,
  onResolve,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
  onResolve?: (encounterId: string, state: CoverageState) => void;
}) => {
  const { count, hasMore, isLoading, isEmpty, isDenied, isFailed } = useEncounterEvents({
    organizationId,
    encounterId,
    lens,
  });

  const state: CoverageState = isLoading
    ? 'loading'
    : isDenied || isFailed
      ? 'unknown'
      : isEmpty
        ? 'empty'
        : 'present';

  useEffect(() => {
    onResolve?.(encounterId, state);
  }, [onResolve, encounterId, state]);

  if (isLoading) return <CircularProgress size={14} aria-label="Checking event coverage" />;
  if (isDenied) return <Chip size="small" variant="outlined" color="warning" label="PHI gated" />;
  if (isFailed) {
    return (
      <Tooltip title="The activity store did not answer — coverage is unknown, not absent.">
        <Chip size="small" variant="outlined" color="default" label="Unknown" />
      </Tooltip>
    );
  }

  if (isEmpty) {
    return (
      <Chip
        size="small"
        color="error"
        icon={<ErrorOutlineIcon />}
        label="No events recorded"
        sx={{ fontWeight: 700 }}
      />
    );
  }

  return (
    <Chip
      size="small"
      variant="outlined"
      color="success"
      label={hasMore ? `${count}+ events` : `${count} event${count === 1 ? '' : 's'}`}
    />
  );
};

/** The expanded panel: this encounter's stream, newest first. */
export const EncounterEventStream = ({
  organizationId,
  encounterId,
  lens,
}: {
  organizationId: string;
  encounterId: string;
  lens: EventLens;
}) => {
  const { entries, hasMore, isLoading, isEmpty, isDenied, isFailed } = useEncounterEvents({
    organizationId,
    encounterId,
    lens,
  });

  if (isLoading) {
    return (
      <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Box>
    );
  }

  if (isDenied) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        Requires admin PHI access to read this encounter&apos;s events.
      </Alert>
    );
  }

  if (isFailed) {
    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        The activity store did not answer. Event coverage for this encounter is unknown — this is
        not the same as having no events.
      </Alert>
    );
  }

  if (isEmpty) {
    return (
      <Alert
        severity="error"
        icon={<ErrorOutlineIcon />}
        sx={{ my: 1 }}
        data-testid="no-events-recorded"
      >
        <Typography sx={{ fontWeight: 700 }}>No events recorded</Typography>
        <Typography variant="body2">
          Nothing was ever written to the {LENS_LABELS[lens].toLowerCase()} timeline for this
          encounter, yet the worklist places it in a concrete state. That state was derived from
          table reads, not from events — an event-coverage gap.
        </Typography>
      </Alert>
    );
  }

  return (
    <Box sx={{ py: 1 }}>
      <Table size="small" aria-label="Encounter event stream">
        <TableHead>
          <TableRow>
            <TableCell>When</TableCell>
            <TableCell>Event</TableCell>
            <TableCell>Actor</TableCell>
            <TableCell>Resource</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {entries.map(entry => (
            <TableRow key={entry.event_id} hover>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>
                {formatDateTime(entry.occurred_at)}
              </TableCell>
              <TableCell>
                <Chip
                  size="small"
                  variant="outlined"
                  color={entry.is_access ? 'default' : 'info'}
                  label={entry.event_type}
                />
              </TableCell>
              <TableCell>
                <EventActor actor={entry.actor} onBehalfOf={entry.on_behalf_of} />
              </TableCell>
              <TableCell>
                {entry.subject_type ? (
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                    <Typography variant="body2">{entry.subject_type}</Typography>
                    {entry.resource_id && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {shortId(entry.resource_id)}
                      </Typography>
                    )}
                  </Box>
                ) : (
                  '—'
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasMore && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Showing the {EVENT_PROBE_LIMIT} most recent events. This view measures coverage, not
          history — open the organization&apos;s Activity tab for the full timeline.
        </Typography>
      )}
    </Box>
  );
};
